/**
 * 移动端（Android Chrome/夸克、iOS Safari/Chrome 等）音频策略：
 * - AudioContext 优先请求 48 kHz；若硬件/浏览器采用其它速率（如 44.1 kHz），由 audio-processor Worklet 线性重采样至 48 kHz 上行。
 * - 桌面端仍尊重用户在设置中选择的采样率。
 */

import { AudioFrameConstants } from '../constants/AudioFrameConstants';
import { getUplinkMicGain, hasStoredUplinkMicGain, clampUplinkMicGain } from './uplinkMicGain';

/** 与上行 / Opus / MQTT 约定一致的目标采样率 */
export const UPLINK_AUDIO_SAMPLE_RATE = AudioFrameConstants.SAMPLE_RATE;

function envTrim(key: string): string {
  return (process.env[key] || '').trim();
}

/**
 * 手机麦克风电平普遍低于桌面；在 Worklet 内转 Int16 前乘以该系数，PCM 与 Opus 上行共用。
 * 用户可在「个人信息 → 前端配置」调节；未设置时桌面为 1、移动端为 1.45。
 * .env `REACT_APP_UPLINK_MOBILE_GAIN` 仅在用户未保存偏好时作为移动端覆盖项。
 */
const DEFAULT_MOBILE_UPLINK_LINEAR_GAIN = 1.45;

export function getWorkletUplinkLinearGain(): number {
  if (hasStoredUplinkMicGain()) {
    return getUplinkMicGain();
  }
  if (!isMobileAudioEnvironment()) {
    return 1;
  }
  const raw = envTrim('REACT_APP_UPLINK_MOBILE_GAIN');
  if (raw) {
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) {
      return clampUplinkMicGain(n);
    }
  }
  return DEFAULT_MOBILE_UPLINK_LINEAR_GAIN;
}

/**
 * 是否为移动端音频环境（粗略检测，覆盖常见手机浏览器）。
 */
export function isMobileAudioEnvironment(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const md = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
    if (md && typeof md.mobile === 'boolean' && md.mobile) {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      return true;
    }
  } catch {
    /* ignore */
  }
  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

/**
 * 创建 AudioContext 时在构造函数中传入的 sampleRate。
 * - 移动端：固定请求 {@link UPLINK_AUDIO_SAMPLE_RATE}，浏览器支持则上下文即为 48 kHz，否则回落本机默认并由 Worklet 重采样。
 * - 桌面端：使用用户在设置中选择的采样率。
 */
export function getAudioContextConstructorSampleRate(userSettingsSampleRate: number): number {
  if (isMobileAudioEnvironment()) {
    return UPLINK_AUDIO_SAMPLE_RATE;
  }
  return userSettingsSampleRate;
}
